import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, InputNumber, Row, Select, Modal, Typography, Card, Input, AutoComplete } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { loadSingleProduct } from "../../redux/actions/product/detailProductAction";
import { loadAllProductCurrency } from "../../redux/actions/productCurrency/getProductCurrencyAction";
import { loadAllBookPublisher } from "../../redux/actions/bookPublisher/getBookPublisherAction";
import { globalProductSearch } from "../../redux/actions/product/globalSearchAction";
import { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "../../utils/debounce";
import AddProd from "../product/addProd";
import ProductSearchModal from "./ProductSearchModal";
import ProductModals from "../product/components/ProductModals";
import axios from "axios";
import { getReferenceListPriceFromProduct } from "../../utils/productListPriceSync";

const { Text } = Typography;

export default function Products({
	allProducts,
	formData,
	setData,
	updateFormData,
	selectedProds,
	handleSelectedProdsQty,
	handleDeleteProd,
	handleSelectedProds,
	handleSelectedProdsBulk,
	handleSelectedProdsSalePrice,
	handleSelectedProdsSaleDisc,
	handleSelectedProdsCurrency,
	handleAddNewProductByISBN,
	handleUpdateNewProductField,
	form,
	getConversionRate,
}

) {
	// Helper function to get product by key
	const getProductByKey = (key) => selectedProds.find(prod => prod.key === key);
	const scrollContainerRef = useRef(null);
	const addRowRef = useRef(null);
	const prevLenRef = useRef((selectedProds || []).length);
	const [productDiscount, setProductDiscount] = useState(0);
	const [addProdVisible, setAddProdVisible] = useState(false);
	const [searchValues, setSearchValues] = useState({});
	const [filteredProducts, setFilteredProducts] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [productSearchModalVisible, setProductSearchModalVisible] = useState(false);
	const [publisherSearchValue, setPublisherSearchValue] = useState({});
	const [showPublisherAdd, setShowPublisherAdd] = useState({});
	const [isAddPublisherModal, setIsAddPublisherModal] = useState(false);
	const [prefillPublisherText, setPrefillPublisherText] = useState('');
	const [publisherModalKey, setPublisherModalKey] = useState(null);

	const [productData, setProductData] = useState(
		selectedProds.map((p) => ({ ...p, productDiscount: 0 }))
	);

	// Load currencies, publishers and global search results
	const allCurrencies = useSelector((state) => state.productCurrencies?.list);
	const allPublishers = useSelector((state) => state.bookPublishers?.list);
	const globalSearchResults = useSelector((state) => state.products?.globalSearchResults);
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(loadAllProductCurrency({ page: 1, limit: 100 }));
		dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
	}, [dispatch]);

	// Debug currencies loading
	useEffect(() => {
		console.log("Currencies loaded:", allCurrencies);
	}, [allCurrencies]);

	// Debug selected products
	useEffect(() => {
		console.log("Selected products:", selectedProds);
	}, [selectedProds]);

	// Auto-scroll reliably when a new product is added
	useEffect(() => {
		const len = (selectedProds || []).length;
		const prev = prevLenRef.current;
		prevLenRef.current = len;
		if (len > prev) {
			requestAnimationFrame(() => {
				setTimeout(() => {
					if (scrollContainerRef.current) {
						scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
						const inputs = scrollContainerRef.current.querySelectorAll('.ant-select-selection-search-input');
						if (inputs.length > 0) inputs[inputs.length - 1].focus();
					}
				}, 80);
			});
		}
	}, [selectedProds]);

	// Sync Form.List when selectedProds changes (bulk select, AI upload, prefill)
	// Keep form row count = selectedProds + 1 blank row; always refresh line values
	useEffect(() => {
		if (form && selectedProds) {
			const expectedCount = Math.max(1, selectedProds.length + 1);
			const formProducts = selectedProds.map((prod) => ({
				product_id: prod.id || null,
				product_quantity: prod.selectedQty || 1,
				product_sale_price: prod.sale_price || 0,
				product_sale_discount: prod.sale_disc || 0,
				product_name: prod.name || "",
			}));
			while (formProducts.length < expectedCount) {
				formProducts.push({
					product_id: null,
					product_quantity: 1,
					product_sale_price: 0,
					product_sale_discount: 0,
					product_name: "",
				});
			}

			const currentValues = form.getFieldsValue();
			form.setFieldsValue({
				...currentValues,
				saleInvoiceProduct: formProducts,
			});

			if (selectedProds.length > prevLenRef.current) {
				setTimeout(() => {
					if (scrollContainerRef.current) {
						scrollContainerRef.current.scrollTop =
							scrollContainerRef.current.scrollHeight;
					}
				}, 150);
			}
			prevLenRef.current = selectedProds.length;
		}
	}, [selectedProds, form]);

	// Initialize filtered products when allProducts changes
	useEffect(() => {
		if (allProducts) {
			setFilteredProducts(allProducts);
		}
	}, [allProducts]);

	// console.log ("productDiscount", productDiscount)

	const getItemTotal = (item) => {
		if (!item || typeof item !== "object") return 0;
		var totalPrice = item.product_quantity * item.product_sale_price;
		if (isNaN(totalPrice) || totalPrice === null || totalPrice === undefined) {
			totalPrice = 0;
		}
		console.log("product Discount = ", item.productDiscount)
		// if (Number(productDiscount)) totalPrice *= (100 - Number(productDiscount)) / 100; //to consider product discount
		return totalPrice;
	};

	const handleProductDiscountChange = (index, discount) => {
		setProductData([
			...productData.slice(0, index),
			{ ...productData[index], productDiscount: discount },
			...productData.slice(index + 1),
		]);
	};

	// Debounced search function for server-side search with pagination
	const debouncedSearch = useCallback(
		debounce(async (searchTerm) => {
			if (!searchTerm || searchTerm.trim() === '') {
				setFilteredProducts(allProducts || []);
				setIsSearching(false);
				return;
			}

			setIsSearching(true);
			try {
				// Use Redux action for global search with pagination
				const result = await dispatch(globalProductSearch(searchTerm.trim(), 1, 50));
				if (result && result.status === "success") {
					setFilteredProducts(result.data || []);
				}
			} catch (error) {
				console.error('Search error:', error);
				// Fallback to client-side filtering
				const filtered = allProducts?.filter(product =>
					product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					product.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					product.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					product.book_publisher?.name?.toLowerCase().includes(searchTerm.toLowerCase())
				) || [];
				setFilteredProducts(filtered);
			} finally {
				setIsSearching(false);
			}
		}, 500), // Increased delay to 500ms for better performance
		[allProducts, dispatch]
	);

	// Function to handle search in Select - now uses debounced server-side search
	const handleSearch = (value, key) => {
		setSearchValues(prev => ({ ...prev, [key]: value }));
		debouncedSearch(value);
	};

	// Function to check if ISBN exists in database
	const checkISBNExists = async (isbn) => {
		if (!isbn || !isbn.trim()) return null;

		try {
			// Use the global search API to find product by ISBN
			const result = await dispatch(globalProductSearch(isbn.trim(), 1, 10));
			if (result && result.status === "success" && result.data) {
				// Check if any result matches the exact ISBN
				const exactMatch = result.data.find(p => p.isbn === isbn.trim());
				return exactMatch || null;
			}
			return null;
		} catch (error) {
			console.error('Error checking ISBN:', error);
			return null;
		}
	};

	// Function to handle ISBN input - check if exists, if not allow manual entry
	const handleISBNInput = async (isbn, key, addCallback) => {
		if (!isbn || !isbn.trim()) return;

		const existingProduct = await checkISBNExists(isbn);

		if (existingProduct) {
			// Product exists - auto-populate
			handleSelectedProds(existingProduct.id, key);
			if (addCallback) addCallback();
		} else {
			// Product doesn't exist - allow manual entry
			if (handleAddNewProductByISBN) {
				handleAddNewProductByISBN(isbn, key);
				if (addCallback) addCallback();
			}
		}
	};

	// Function to handle Add Product click
	const handleAddProduct = () => {
		setAddProdVisible(true);
	};

	// Function to handle product selection from modal
	// This function can be called with a pre-generated key for bulk additions
	const handleProductSelectFromModal = (product, providedKey = null) => {
		// Generate a unique key if not provided
		// Use timestamp + random + product ID to ensure uniqueness even when called in quick succession
		const uniqueKey = providedKey !== null
			? providedKey
			: Date.now() + Math.random() + (product.id * 1000000);
		handleSelectedProds(product.id, uniqueKey);
		// Add blank row for next product (same as new purchase)
		addRowRef.current?.({});
	};

	const handleProductsSelectBulkFromModal = (products) => {
		if (typeof handleSelectedProdsBulk === "function") {
			handleSelectedProdsBulk(products);
		} else {
			const baseTimestamp = Date.now();
			(products || []).forEach((product, index) => {
				const uniqueKey =
					baseTimestamp + index + (product.id * 1000000) + Math.random() * 1000;
				handleSelectedProds(product.id, uniqueKey);
			});
		}
	};

	// Function to handle Add New Product from modal
	const handleAddNewProductFromModal = () => {
		setAddProdVisible(true);
		setProductSearchModalVisible(false);
	};

	// Function to handle currency change
	const handleCurrencyChange = (index, currencyId) => {
		const selectedCurrency = allCurrencies?.find(c => c.id === currencyId);
		if (selectedCurrency && selectedProds[index]) {
			const updatedProduct = {
				...selectedProds[index],
				product_currency: selectedCurrency
			};

			// Update the selectedProds array
			const updatedSelectedProds = selectedProds.map((prod, i) =>
				i === index ? updatedProduct : prod
			);

			// Call the parent's handleSelectedProds to update the state
			// We need to find the product ID and call the handler
			handleSelectedProds(updatedProduct.id, index);

			// Then manually update the currency
			const finalUpdatedProds = updatedSelectedProds.map((prod, i) =>
				i === index ? { ...prod, product_currency: selectedCurrency } : prod
			);

			// We need to trigger a re-render by updating the local state
			setProductData(finalUpdatedProds.map((p) => ({ ...p, productDiscount: 0 })));
		}
	};

	return (
		<>
			{/* Select books (checkboxes) — available on mobile and desktop */}
			<div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setProductSearchModalVisible(true)}
					style={{ minWidth: 180, height: 40 }}
				>
					Select Books
				</Button>
				{selectedProds?.length > 0 && (
					<Text type="secondary" style={{ fontSize: 13 }}>
						{selectedProds.length} book{selectedProds.length !== 1 ? "s" : ""} on
						invoice — review qty, price &amp; discount below before saving
					</Text>
				)}
			</div>

			{/* Desktop Product Table */}
			<div className="d-none d-lg-block">
				<Row gutter={[16]}>
					<Col span={1}>
						<div className='font-weight-bold border-b'>SL</div>
					</Col>
					<Col span={4}>
						<div className='font-weight-bold border-b'>Product</div>
					</Col>
					<Col span={2}>
						<div className='font-weight-bold border-b'>Author</div>
					</Col>
					<Col span={2}>
						<div className='font-weight-bold border-b'>Publisher</div>
					</Col>
					<Col span={1}>
						<div className='font-weight-bold border-b'>Curr</div>
					</Col>
					<Col span={2}>
						<div className='font-weight-bold'>List / MRP</div>
					</Col>
					<Col span={1}>
						<div className='font-weight-bold'>GOC</div>
					</Col>
					<Col span={1}>
						<div className='font-weight-bold'>U.M</div>
					</Col>
					<Col span={2}>
						<div className='font-weight-bold'>Quantity</div>
					</Col><Col span={1}>
						<div className='font-weight-bold'>Amt.</div>
					</Col>
					<Col span={2}>
						<div className='font-weight-bold border-b'>Disc(%)</div>
					</Col>
					<Col span={3}>
						<div className='font-weight-bold'>Grs. Amt.</div>
					</Col>
					<Col span={2}>
						<div></div>
					</Col>
				</Row>

				<hr style={{ backgroundColor: "black" }} />

				<Form.List name='saleInvoiceProduct'>
					{(fields, { add, remove }) => {
						addRowRef.current = add;
						return (
						<>
							{/* Scrollable container for product rows */}
							<div
								ref={scrollContainerRef}
								style={{
									maxHeight: '500px', // Height for approximately 6-8 rows
									overflowY: 'auto',
									border: '1px solid #d9d9d9',
									borderRadius: '6px',
									padding: '8px'
								}}
							>
								{fields.map(({ key, name, ...restField }, index) => (
									<Row className='mt-2' gutter={[16]} key={key} data-row-index={index}>

										<Col span={1}>	{index + 1}</Col>	{/* S.No. */}

										<Col span={4}>	{/* ISBN / Title */}
											{(() => {
												// Try multiple strategies to find the product
												let product = null;

												// Strategy 1: Try to get product by key
												product = getProductByKey(key);

												// Strategy 2: If not found by key, try by index (for AI upload scenarios)
												if (!product && selectedProds && selectedProds.length > index) {
													product = selectedProds[index];
												}

												// Strategy 3: If still not found and this row is an existing row (not a newly added empty row),
												// try product_id from form. Skip for index >= selectedProds.length so newly added rows
												// don't show the first product when Form.List copies the first field's values.
												if (!product && index < selectedProds.length) {
													const formProductId = restField.form?.getFieldValue(['saleInvoiceProduct', name, 'product_id']);
													if (formProductId) {
														// Try to find in selectedProds by id
														product = selectedProds.find(p => p.id === formProductId);
														// If still not found, try to find in allProducts and create product object
														if (!product) {
															const productFromStore = allProducts?.find(p => p.id === formProductId);
															if (productFromStore) {
																// Create a product object with the form field values
																const formValues = restField.form?.getFieldsValue(['saleInvoiceProduct', name]);
																product = {
																	...productFromStore,
																	selectedQty: formValues?.product_quantity || 1,
																	sale_price: formValues?.product_sale_price || productFromStore.sale_price || 0,
																	sale_disc: formValues?.product_sale_discount || 0,
																	key: key,
																	reference_list_price: getReferenceListPriceFromProduct(productFromStore),
																};
															}
														}
													}
												}

												const isNewProduct = product?.isNewProduct;

												if (isNewProduct) {
													// For new products, show ISBN (editable) and name (editable)
													return (
														<div>
															<Input
																placeholder="ISBN"
																value={product?.isbn || ""}
																onChange={(e) => {
																	// Update ISBN in the product
																	if (handleUpdateNewProductField) {
																		handleUpdateNewProductField(key, 'isbn', e.target.value);
																	}
																}}
																onBlur={async (e) => {
																	// When user finishes editing ISBN, check if it exists
																	const newIsbn = e.target.value?.trim();
																	if (newIsbn && newIsbn !== product?.isbn) {
																		// Check if the new ISBN exists in database
																		const existingProduct = await checkISBNExists(newIsbn);
																		if (existingProduct) {
																			// If ISBN exists, replace new product with existing one
																			handleSelectedProds(existingProduct.id, key);
																		} else {
																			// Update ISBN if it doesn't exist
																			if (handleUpdateNewProductField) {
																				handleUpdateNewProductField(key, 'isbn', newIsbn);
																			}
																		}
																	}
																}}
																size="small"
																style={{ marginBottom: '4px' }}
															/>
															<Form.Item
																{...restField}
																name={[name, "product_name"]}
																rules={[
																	{
																		required: true,
																		message: 'Product Name is required',
																		validator: (_, value) => {
																			const product = getProductByKey(key);
																			if (product?.isNewProduct) {
																				const productName = value || product?.name || '';
																				if (!productName || !productName.trim()) {
																					return Promise.reject(new Error('Product Name is required'));
																				}
																			}
																			return Promise.resolve();
																		}
																	}
																]}
																style={{ marginBottom: 0 }}
																initialValue={product?.name || ""}
															>
																<Input
																	placeholder="Product Name *"
																	value={product?.name || ""}
																	onChange={(e) => {
																		if (handleUpdateNewProductField) {
																			handleUpdateNewProductField(key, 'name', e.target.value);
																		}
																		// Trigger validation
																		const form = restField.form;
																		if (form) {
																			form.validateFields([['saleInvoiceProduct', name, 'product_name']]);
																		}
																	}}
																	onBlur={() => {
																		// Trigger validation on blur
																		const form = restField.form;
																		if (form) {
																			form.validateFields([['saleInvoiceProduct', name, 'product_name']]);
																		}
																	}}
																	size="small"
																/>
															</Form.Item>
														</div>
													);
												} else {
													// For existing products, use AutoComplete with search
													// Always use ISBN from product object, not from form field

													// If product is found but missing ISBN, try to get it from allProducts
													if (product && !product.isbn && product.id) {
														const productFromStore = allProducts?.find(p => p.id === product.id);
														if (productFromStore) {
															product = { ...product, ...productFromStore };
														}
													}

													// Product should already be found above, but double-check
													// For display, we'll show ISBN in AutoComplete and title below it
													let isbnValue = '';
													let productName = '';

													if (product && product.isbn) {
														isbnValue = product.isbn;
														productName = product.name || '';
													} else if (searchValues[key]) {
														isbnValue = searchValues[key];
														productName = '';
													} else {
														// Last resort: try to get from form field product_id
														const formProductId = restField.form?.getFieldValue(['saleInvoiceProduct', name, 'product_id']);
														if (formProductId) {
															const productFromStore = allProducts?.find(p => p.id === formProductId);
															if (productFromStore && productFromStore.isbn) {
																isbnValue = productFromStore.isbn;
																productName = productFromStore.name || '';
																// Update the product object for future use
																if (!product) {
																	const formValues = restField.form?.getFieldsValue(['saleInvoiceProduct', name]);
																	product = {
																		...productFromStore,
																		selectedQty: formValues?.product_quantity || 1,
																		sale_price: formValues?.product_sale_price || productFromStore.sale_price || 0,
																		sale_disc: formValues?.product_sale_discount || 0,
																		key: key,
																		reference_list_price: getReferenceListPriceFromProduct(productFromStore),
																	};
																}
															} else {
																// If product not found in store, show placeholder instead of ID
																isbnValue = 'Product not found';
																productName = '';
															}
														} else {
															isbnValue = '';
															productName = '';
														}
													}

													// Ensure we never show product_id - always show ISBN
													// If isbnValue is just a number (product_id), look it up again
													if (isbnValue && /^\d+$/.test(isbnValue.trim()) && !product?.isbn) {
														// This looks like a product_id, not an ISBN
														const productId = parseInt(isbnValue.trim());
														const productFromStore = allProducts?.find(p => p.id === productId);
														if (productFromStore && productFromStore.isbn) {
															isbnValue = productFromStore.isbn;
															productName = productFromStore.name || '';
														} else {
															isbnValue = ''; // Clear invalid display value
															productName = '';
														}
													}

													// Use ISBN for AutoComplete value (for search functionality)
													const displayValue = isbnValue;

													return (
														<>
															{/* Hidden input to store product_id for form submission */}
															<Form.Item {...restField} name={[name, "product_id"]} noStyle>
																<input type="hidden" />
															</Form.Item>
															<div style={{ position: 'relative' }}>
																<AutoComplete
																	placeholder={isbnValue && !searchValues[key] ? '' : 'Enter ISBN or Search'}
																	value={searchValues[key] || ''}
																	style={{
																		width: '100%',
																		minHeight: (productName && !searchValues[key]) ? '48px' : '32px'
																	}}
																	className="product-isbn-autocomplete-multiline"
																	dropdownClassName="product-isbn-search-dropdown"
																	dropdownMatchSelectWidth={false}
																	dropdownStyle={{
																		minWidth: 560,
																		maxWidth: 720,
																		width: Math.max(560, (typeof window !== 'undefined' ? Math.min(window.innerWidth - 48, 720) : 560)),
																	}}
																	// Custom suffix to show ISBN and title
																	suffixIcon={null}
																	options={(() => {
																		const currentOptions = searchValues[key] ? filteredProducts : allProducts;
																		const productOptions = currentOptions?.slice(0, 10).map((p) => {
																			const price =
																				p.sale_price != null && p.sale_price !== ""
																					? `₹${Number(p.sale_price).toLocaleString("en-IN", {
																							minimumFractionDigits: 0,
																							maximumFractionDigits: 2,
																					  })}`
																					: "—";
																			const author = p.author || "—";
																			const publisher = p.book_publisher?.name || "—";
																			return {
																				value: p.isbn,
																				label: (
																					<div
																						className="product-isbn-search-option"
																						style={{
																							display: "grid",
																							gridTemplateColumns: "minmax(160px, 1.4fr) minmax(90px, 1fr) minmax(90px, 1fr) 72px",
																							gap: 10,
																							alignItems: "start",
																							minWidth: 520,
																							padding: "4px 0",
																						}}
																					>
																						<div style={{ minWidth: 0 }}>
																							<div style={{ fontWeight: 600, fontSize: 12 }}>{p.isbn}</div>
																							<div
																								style={{
																									fontSize: 12,
																									color: "#666",
																									marginTop: 2,
																									overflow: "hidden",
																									textOverflow: "ellipsis",
																									whiteSpace: "nowrap",
																								}}
																								title={p.name || ""}
																							>
																								{p.name || "Untitled"}
																							</div>
																						</div>
																						<div
																							style={{
																								fontSize: 12,
																								color: "#444",
																								overflow: "hidden",
																								textOverflow: "ellipsis",
																								whiteSpace: "nowrap",
																							}}
																							title={author}
																						>
																							<span style={{ color: "#999", fontSize: 10, display: "block" }}>Author</span>
																							{author}
																						</div>
																						<div
																							style={{
																								fontSize: 12,
																								color: "#444",
																								overflow: "hidden",
																								textOverflow: "ellipsis",
																								whiteSpace: "nowrap",
																							}}
																							title={publisher}
																						>
																							<span style={{ color: "#999", fontSize: 10, display: "block" }}>Publisher</span>
																							{publisher}
																						</div>
																						<div
																							style={{
																								fontSize: 12,
																								fontWeight: 600,
																								color: "#389e0d",
																								textAlign: "right",
																								whiteSpace: "nowrap",
																							}}
																						>
																							<span style={{ color: "#999", fontSize: 10, display: "block", fontWeight: 400 }}>Price</span>
																							{price}
																						</div>
																					</div>
																				),
																			};
																		}) || [];

																		// Add "Add New Product" option if there's a search value
																		if (searchValues[key] && searchValues[key].trim()) {
																			return [
																				...productOptions,
																				{
																					value: '__add_new_product__',
																					label: (
																						<div style={{
																							lineHeight: '1.2',
																							color: '#1890ff',
																							fontWeight: 'bold',
																							padding: '4px 0',
																							borderTop: '1px solid #f0f0f0',
																							marginTop: '4px',
																							minWidth: 520,
																						}}>
																							<PlusOutlined /> Add New Product: "{searchValues[key]}"
																						</div>
																					)
																				}
																			];
																		}
																		return productOptions;
																	})()}
																	onSearch={(value) => {
																		setSearchValues(prev => ({ ...prev, [key]: value }));
																		debouncedSearch(value);
																	}}
																	onSelect={(selectedValue) => {
																		if (selectedValue === '__add_new_product__') {
																			const isbnValue = searchValues[key]?.trim();
																			if (isbnValue) {
																				if (handleAddNewProductByISBN) {
																					handleAddNewProductByISBN(isbnValue, key);
																				}
																				add({});
																				setAddProdVisible(true);
																				setSearchValues(prev => ({ ...prev, [key]: "" }));
																			}
																		} else {
																			const selectedProduct = allProducts?.find(p => p.isbn === selectedValue) ||
																				filteredProducts?.find(p => p.isbn === selectedValue);

																			if (selectedProduct) {
																				handleSelectedProds(selectedProduct.id, key);
																				add({});
																				setSearchValues(prev => ({ ...prev, [key]: "" }));
																				setFilteredProducts(allProducts || []);
																			}
																		}
																	}}
																	onKeyDown={(e) => {
																		if (e.key === 'Enter') {
																			e.preventDefault();
																			const value = e.target.value?.trim();
																			if (value && !product) {
																				if (/^[A-Z0-9-]+$/i.test(value)) {
																					handleISBNInput(value, key, add);
																				}
																			}
																		}
																	}}
																	onBlur={async (e) => {
																		const value = e.target.value?.trim();
																		if (value && !product) {
																			if (/^[A-Z0-9-]+$/i.test(value)) {
																				await handleISBNInput(value, key, add);
																			}
																		}
																	}}
																	filterOption={false}
																	notFoundContent={
																		searchValues[key] && filteredProducts.length === 0 && !isSearching ? (
																			<div style={{ textAlign: 'center', padding: '10px' }}>
																				<div style={{ marginBottom: '8px' }}>No products found for "{searchValues[key]}"</div>
																				<div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
																					You can add it directly in the cells or use the modal
																				</div>
																				<Button
																					type="primary"
																					size="small"
																					icon={<PlusOutlined />}
																					onClick={() => {
																						const isbnValue = searchValues[key]?.trim();
																						if (isbnValue && handleAddNewProductByISBN) {
																							handleAddNewProductByISBN(isbnValue, key);
																							add({});
																						}
																						setAddProdVisible(true);
																					}}
																				>
																					+ Add New Product
																				</Button>
																			</div>
																		) : null
																	}
																/>
																{/* Custom display overlay - shows ISBN and title inside the input box when not searching */}
																{!searchValues[key] && isbnValue && (
																	<div
																		style={{
																			position: 'absolute',
																			top: '1px',
																			left: '11px',
																			right: '32px',
																			padding: '4px 0',
																			pointerEvents: 'none',
																			zIndex: 1,
																			display: 'flex',
																			flexDirection: 'column',
																			justifyContent: 'center',
																			minHeight: productName ? '44px' : '24px',
																			lineHeight: '1.5'
																		}}
																	>
																		<div style={{ fontWeight: '500', fontSize: '13px', color: '#000' }}>{isbnValue}</div>
																		{productName && (
																			<div style={{
																				fontSize: '11px',
																				color: '#666',
																				marginTop: '2px',
																				lineHeight: '1.3',
																				wordWrap: 'break-word',
																				whiteSpace: 'normal'
																			}}>
																				{productName}
																			</div>
																		)}
																	</div>
																)}
															</div>
														</>
													);
												}
											})()}
										</Col>

										<Col span={2}>	{/* Author */}
											{(() => {
												const product = getProductByKey(key);
												const isNewProduct = product?.isNewProduct;
												if (isNewProduct) {
													return (
														<Input
															placeholder="Author"
															value={product?.author || ""}
															onChange={(e) => handleUpdateNewProductField && handleUpdateNewProductField(key, 'author', e.target.value)}
															size="small"
														/>
													);
												}
												return <div className='font-weight-bold'>{product?.author || ""}</div>;
											})()}
										</Col>

										<Col span={2}>	{/* Publisher */}
											{(() => {
												const product = getProductByKey(key);
												const isNewProduct = product?.isNewProduct;
												if (isNewProduct && allPublishers) {
													return (
														<Select
															size="small"
															placeholder="Publisher"
															showSearch
															optionFilterProp="children"
															filterOption={(input, option) => {
																if (option.value === '__add_publisher__') return true;
																return option.children?.toLowerCase().includes(input.toLowerCase());
															}}
															value={product?.book_publisher?.id || null}
															onChange={(publisherId) => {
																if (publisherId === '__add_publisher__') {
																	const searchVal = publisherSearchValue[key] || '';
																	setPrefillPublisherText(searchVal);
																	setPublisherModalKey(key);
																	setIsAddPublisherModal(true);
																} else {
																	const selectedPublisher = allPublishers.find(p => p.id === publisherId);
																	if (handleUpdateNewProductField && selectedPublisher) {
																		handleUpdateNewProductField(key, 'book_publisher', selectedPublisher);
																	}
																	setPublisherSearchValue(prev => ({ ...prev, [key]: "" }));
																	setShowPublisherAdd(prev => ({ ...prev, [key]: false }));
																}
															}}
															onSearch={(value) => {
																setPublisherSearchValue(prev => ({ ...prev, [key]: value }));
																if (value && allPublishers) {
																	const hasMatch = allPublishers.some(pub =>
																		pub.name.toLowerCase().includes(value.toLowerCase())
																	);
																	setShowPublisherAdd(prev => ({ ...prev, [key]: !hasMatch && value.trim() !== '' }));
																} else {
																	setShowPublisherAdd(prev => ({ ...prev, [key]: false }));
																}
															}}
															style={{ width: '100%' }}
															allowClear
															notFoundContent={
																showPublisherAdd[key] && publisherSearchValue[key] ? (
																	<div style={{ padding: '8px', textAlign: 'center' }}>
																		<div style={{ marginBottom: '8px', color: '#666' }}>
																			No publisher found for "{publisherSearchValue[key]}"
																		</div>
																		<Button
																			type="primary"
																			size="small"
																			icon={<PlusOutlined />}
																			onClick={() => {
																				setPrefillPublisherText(publisherSearchValue[key]);
																				setPublisherModalKey(key);
																				setIsAddPublisherModal(true);
																			}}
																			style={{ width: '100%' }}
																		>
																			Add "{publisherSearchValue[key]}"
																		</Button>
																	</div>
																) : null
															}
														>
															{allPublishers.map(publisher => (
																<Select.Option key={publisher.id} value={publisher.id}>
																	{publisher.name}
																</Select.Option>
															))}
															{showPublisherAdd[key] && publisherSearchValue[key] && (
																<Select.Option value="__add_publisher__">
																	<div style={{ color: '#1890ff', fontWeight: 'bold' }}>
																		<PlusOutlined /> Add "{publisherSearchValue[key]}"
																	</div>
																</Select.Option>
															)}
														</Select>
													);
												}
												return <div className='font-weight-bold'>{product?.book_publisher?.name || ""}</div>;
											})()}
										</Col>

										<Col span={1}>	{/* Currency */}
											{(() => {
												const product = getProductByKey(key);
												const isNewProduct = product?.isNewProduct;
												if (isNewProduct && allCurrencies) {
													return (
														<Select
															size="small"
															value={product?.product_currency?.id || allCurrencies[0]?.id}
															onChange={(currencyId) => {
																const selectedCurrency = allCurrencies.find(c => c.id === currencyId);
																if (handleUpdateNewProductField && selectedCurrency) {
																	handleUpdateNewProductField(key, 'product_currency', selectedCurrency);
																}
															}}
															style={{ width: '100%' }}
														>
															{allCurrencies.map(currency => (
																<Select.Option key={currency.id} value={currency.id}>
																	{currency.symbol}
																</Select.Option>
															))}
														</Select>
													);
												}
												return <div className='font-weight-bold'>{product?.product_currency?.symbol || ""}</div>;
											})()}
										</Col>

										<Col span={2}>	{/** Product Sale price */}
											<Form.Item {...restField} name={[name, "product_sale_price"]}>
												<InputNumber
													style={{ width: "100%" }}
													placeholder='List / MRP'
													onChange={(salePrice) => {
														const product = getProductByKey(key);
														if (product?.isNewProduct && handleUpdateNewProductField) {
															handleUpdateNewProductField(key, 'sale_price', salePrice);
														}
														handleSelectedProdsSalePrice(key, salePrice);
													}}
													value={
														getProductByKey(key)?.sale_price || ""
													}
												/>
												<p style={{ display: "none" }}>
													{getProductByKey(key)?.sale_price || ""}
												</p>
											</Form.Item>
										</Col>

										<Col span={1}>	{/* Conversion */}
											<div className='font-weight-bold'>
												{(() => {
													const product = getProductByKey(key);
													if (!product) return "";
													const currencyId = product.product_currency?.id;
													const defaultConversion = product.product_currency?.conversion || 1;
													return getConversionRate ? getConversionRate(currencyId, defaultConversion) : defaultConversion;
												})()}
											</div>
										</Col>

										<Col span={1}>	{/*Unit Measurement*/}
											{(() => {
												const product = getProductByKey(key);
												const isNewProduct = product?.isNewProduct;
												if (isNewProduct) {
													return (
														<InputNumber
															size="small"
															placeholder="U.M"
															value={product?.unit_measurement || 0}
															onChange={(value) => handleUpdateNewProductField && handleUpdateNewProductField(key, 'unit_measurement', value)}
															style={{ width: '100%' }}
															min={0}
														/>
													);
												}
												return <div className='font-weight-bold'>{product?.unit_measurement || ""}</div>;
											})()}
										</Col>

										<Col span={2}>	{/* Quantity */}
											<Form.Item {...restField} name={[name, "product_quantity"]}>
												<InputNumber
													style={{ width: "100%" }}
													placeholder='Product Quantity'
													onChange={(qty) => handleSelectedProdsQty(key, qty)}
													value={
														getProductByKey(key)?.selectedQty || ""
													}
												/>
												<p style={{ display: "none" }}>
													{getProductByKey(key)?.selectedQty || ""}
												</p>
											</Form.Item>
										</Col>

										<Col span={1}> {/** Amount */}
											<div className='font-weight-bold'>
												{(() => {
													const product = getProductByKey(key);
													if (!product) return "0";
													const currencyId = product.product_currency?.id;
													const defaultConversion = product.product_currency?.conversion || 1;
													const conversion = getConversionRate ? getConversionRate(currencyId, defaultConversion, product.product_currency) : defaultConversion;
													return (product.selectedQty * product.sale_price * conversion || 0);
												})()}
											</div>
										</Col>

										<Col span={2}>	{/** Product Discount */}

											<Form.Item {...restField} name={[name, "product_sale_discount"]}>
												<InputNumber
													style={{ width: "100%" }}
													placeholder='Discount percentage'
													onChange={(saleDisc) =>
														handleSelectedProdsSaleDisc(key, saleDisc)
													}
													value={
														getProductByKey(key)?.sale_disc || ""
													}
													min={0}
													max={100}
												/>
												<p style={{ display: "none" }}>
													{getProductByKey(key)?.sale_disc || ""}
												</p>
												{/* <InputNumber
									onChange={(e) => handleProductDiscountChange(e)}
									defaultValue={productDiscount}
									label='Disc'
									name='prodDisc'
								/> */}
											</Form.Item>
										</Col>


										<Col span={3}> {/** Gross Amount */}
											<div className='font-weight-bold'>
												{(() => {
													const product = getProductByKey(key);
													if (!product) return "0.00";
													const currencyId = product.product_currency?.id;
													const defaultConversion = product.product_currency?.conversion || 1;
													const conversion = getConversionRate ? getConversionRate(currencyId, defaultConversion, product.product_currency) : defaultConversion;
													const gross = conversion *
														(product.selectedQty || 0) *
														(product.sale_price || 0);
													const discount = gross * ((product.sale_disc || 0) / 100);
													return (gross - discount).toFixed(2);
												})()}
											</div>
										</Col>
										<Col span={2}> {/** Delete Icon */}
											<Form.Item>
												<Button
													shape='circle'
													icon={<DeleteOutlined />}
													onClick={() => {
														remove(name);
														handleDeleteProd(key);
													}}></Button>
											</Form.Item>
										</Col>
									</Row>
								))}
							</div>

							<Form.Item style={{ marginTop: "20px" }}>
								<Button
									type='dashed'
									onClick={() => {
										// Pass empty object so new row doesn't copy first row's values
										add({});
										// Auto-scroll to new row and focus its input
										setTimeout(() => {
											if (scrollContainerRef.current) {
												scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
												const inputs = scrollContainerRef.current.querySelectorAll('.ant-select-selection-search-input');
												if (inputs.length > 0) {
													inputs[inputs.length - 1].focus();
												}
											}
										}, 150);
									}}
									block
									icon={<PlusOutlined />}>
									Add
								</Button>
							</Form.Item>
						</>
					);
					}}
				</Form.List>
			</div>

			{/* Mobile Product List */}
			<div className="d-lg-none">
				{selectedProds && selectedProds.length > 0 ? (
					<div className="row">
						{selectedProds.map((product, index) => (
							<div key={index} className="col-12 mb-3">
								<Card size="small">
									{/* Product Information */}
									<div className="mb-3">
										<Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
											{product.name}
										</Text>
										{/* Debug info */}
										<div style={{ fontSize: '10px', color: '#999', marginBottom: '5px' }}>
											Debug - Currency ID: {product.product_currency?.id || product.product_currency_id || 'undefined'} |
											Currency: {product.product_currency?.name || 'undefined'} |
											Symbol: {product.product_currency?.symbol || 'undefined'}
										</div>
										<div className="row">
											<div className="col-6">
												<Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
													<strong>ISBN:</strong> {product.isbn}
												</Text>
											</div>
											<div className="col-6">
												<Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
													<strong>Author:</strong> {product.author || 'N/A'}
												</Text>
											</div>
										</div>
										<div className="row mt-1">
											<div className="col-12">
												<Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
													<strong>Publisher:</strong> {product.book_publisher?.name || 'N/A'}
												</Text>
											</div>
										</div>
									</div>

									{/* Editable Fields */}
									<div className="row">
										<div className="col-4">
											<Text style={{ fontSize: '12px', fontWeight: 'bold' }}>Currency:</Text>
											<Select
												size="small"
												style={{ width: '100%' }}
												value={product.product_currency?.id || product.product_currency_id}
												loading={!allCurrencies}
												placeholder="Select Currency"
												onChange={(currencyId) => {
													console.log("Currency changed to:", currencyId);
													if (handleSelectedProdsCurrency) {
														handleSelectedProdsCurrency(product.key, currencyId);
													} else {
														// Fallback: try to update currency directly
														const fallbackIndex = selectedProds.findIndex(
															(prod) => prod.key === product.key
														);
														if (fallbackIndex !== -1) {
															handleCurrencyChange(fallbackIndex, currencyId);
														}
													}
												}}
											>
												{allCurrencies && allCurrencies.length > 0 ? (
													allCurrencies.map(currency => (
														<Select.Option key={currency.id} value={currency.id}>
															{currency.symbol} ({currency.name})
														</Select.Option>
													))
												) : (
													<Select.Option disabled value="no-currencies">
														No currencies available
													</Select.Option>
												)}
											</Select>
										</div>
										<div className="col-4">
											<Text style={{ fontSize: '12px', fontWeight: 'bold' }}>List / MRP:</Text>
											<InputNumber
												min={0}
												value={product.sale_price || 0}
												onChange={(value) => handleSelectedProdsSalePrice(product.key, value)}
												style={{ width: '100%' }}
												size="small"
											/>
										</div>
										<div className="col-4">
											<Text style={{ fontSize: '12px', fontWeight: 'bold' }}>Discount(%):</Text>
											<InputNumber
												min={0}
												max={100}
												value={product.sale_disc || 0}
												onChange={(value) => handleSelectedProdsSaleDisc(product.key, value)}
												style={{ width: '100%' }}
												size="small"
											/>
										</div>
									</div>

									{/* Quantity and Actions */}
									<div className="row mt-2">
										<div className="col-6">
											<Text style={{ fontSize: '12px', fontWeight: 'bold' }}>Quantity:</Text>
											<InputNumber
												min={1}
												value={product.selectedQty || 1}
												onChange={(value) => handleSelectedProdsQty(product.key, value)}
												style={{ width: '100%' }}
												size="small"
											/>
										</div>
										<div className="col-6 d-flex align-items-end">
											<Button
												type="text"
												danger
												icon={<DeleteOutlined />}
												onClick={() => handleDeleteProd(product.key)}
												size="small"
												style={{ marginLeft: 'auto' }}
											>
												Remove
											</Button>
										</div>
									</div>

									{/* Total Amount Display */}
									<div className="mt-2 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
										<div className="d-flex justify-content-between">
											<Text style={{ fontSize: '12px' }}>Total Amount:</Text>
											<Text strong style={{ fontSize: '14px' }}>
												{(() => {
													const currencyId = product.product_currency?.id;
													const defaultConversion = product.product_currency?.conversion || 1;
													const conversion = getConversionRate ? getConversionRate(currencyId, defaultConversion) : defaultConversion;
													return `${product.product_currency?.symbol || '₹'}${((product.selectedQty || 1) * (product.sale_price || 0) * conversion * (1 - (product.sale_disc || 0) / 100)).toFixed(2)}`;
												})()}
											</Text>
										</div>
									</div>
								</Card>
							</div>
						))}
					</div>
				) : (
					<div className="text-center py-4">
						<Text type="secondary">No products added yet</Text>
					</div>
				)}
			</div>

			{/* Product Search Modal — checkbox multi-select → prefill invoice lines */}
			<ProductSearchModal
				visible={productSearchModalVisible}
				onClose={() => setProductSearchModalVisible(false)}
				allProducts={allProducts}
				selectedProds={selectedProds}
				onProductSelect={handleProductSelectFromModal}
				onProductsSelectBulk={handleProductsSelectBulkFromModal}
				onAddNewProduct={handleAddNewProductFromModal}
			/>

			{/* Add Product Modal */}
			<Modal
				title="Add New Product"
				open={addProdVisible}
				onCancel={() => setAddProdVisible(false)}
				footer={null}
				width={1200}
				style={{ top: 20 }}
				bodyStyle={{ paddingTop: 12 }}
				destroyOnClose={true}
			>
				<AddProd
					onClose={() => {
						setAddProdVisible(false);
						// Optionally refresh products list here
						window.location.reload(); // Simple refresh - you might want to use Redux action instead
					}}
				/>
			</Modal>

			{/* Add Publisher Modal */}
			<ProductModals
				isAdd={isAddPublisherModal}
				type="publisher"
				prefillPublisherText={prefillPublisherText}
				onCloseAdd={async (refresh) => {
					const currentKey = publisherModalKey;
					setIsAddPublisherModal(false);
					setPrefillPublisherText('');
					setPublisherModalKey(null);

					if (refresh && currentKey) {
						// Reload publishers after adding
						await dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
						setTimeout(() => {
							setPublisherSearchValue(prev => ({ ...prev, [currentKey]: "" }));
							setShowPublisherAdd(prev => ({ ...prev, [currentKey]: false }));
						}, 500);
					}
				}}
			/>
		</>
	);
}

// {
//   "total_amount": 10,
//   "due_amount": 1,
//   "paid_amount": 11,
//   "supplier_id":1,
//   "purchaseInvoiceProduct": [
//       {
//           "product_id": 1,
//           "product_quantity": 1,
//           "product_purchase_price": 500
//       },
//       {
//           "product_id": 2,
//           "product_quantity": 10,
//           "product_purchase_price": 800
//       }
//   ]
// }
